/**
 * patrol-tracking-edit.js
 */

import React from 'react';
import { Alert, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { parseISO, formatISO9075, isValid, differenceInHours } from 'date-fns';
import { Types } from 'utils/constants';
import { displayNumber } from 'utils/misc';
import { UserContext } from 'contexts/user/user-context';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import FormGridContainer from 'components/menu-drawer/form-grid-container';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import HiddenInput from 'components/form-inputs/hidden-input';
import CheckboxList from 'components/form-inputs/checkbox-list';
import TextField from 'components/form-inputs/text-field';
import Select from 'components/form-inputs/select';
import DateTimePicker from 'components/form-inputs/date-time-picker';
import { DeploymentMap, fetchTracks } from 'components/tracking/map/viewing-map';
import { TRACK_TYPE } from 'components/tracking/tracking-common';
import PatrolArrestsBitesEdit from '../../helpers/patrol-arrests-bites-edit';
import { convertDistance } from 'utils/distance';


export default function PatrolTrackingEdit(props) {
  const {
    deployment,
    drawerState,
    edit,
    patrolIndex,
    isAdd
  } = props;
  const formNameBase = `patrol.patrolTypes[${patrolIndex}].tracking`;
  const { t } = useTranslation(edit.translation);
  const { setValue, control } = useFormContext();
  const distanceUnits = edit.options.distanceUnits;
  const distanceUnitValue = useWatch({ control, name: `${formNameBase}.trackDistanceUnit` });
  const distance = useWatch({ control, name: `${formNameBase}.trackDistance` });
  const trackingRecord = deployment.patrol.patrolTypes[patrolIndex]?.tracking;
  // the distance and unit first entered, used for more accurate conversions between units.
  const [distanceInput, setDistanceInput] = React.useState({ 
    value: distance, 
    unit: distanceUnitValue?.id 
  });
  const { user } = React.useContext(UserContext);
  const [tracks, setTracks] = React.useState();


  // if units is null, default to miles
  React.useEffect(() => {
    if (!distanceUnitValue) {
      setValue(`${formNameBase}.trackDistanceUnit`, distanceUnits[0]);
    }
  }, [distanceUnitValue, formNameBase, distanceUnits, setValue]);


  React.useEffect(() => {
    if (trackingRecord?.hasTrack && trackingRecord?.id && edit?.record?.id && user) {
      (async () => {
        const localTracks = await fetchTracks(TRACK_TYPE.DEPLOYMENT, edit.record.id, trackingRecord.id, user);
        setTracks(localTracks);
      })();
    }
  }, [edit?.record?.id, trackingRecord?.id, trackingRecord?.hasTrack, user]);


  // track might still be active if it doesn't have the complete flag set 
  // (i.e. if a save/complete button wasn't pressed) and if it's been less
  // than 3 hours since the last point was created.
  const isTrackActive = tracks?.[0] 
    && !tracks[0].isComplete 
    && differenceInHours(new Date(), tracks[0].points[tracks[0].points.length - 1].dateTimeCreated) < 3;


  // convert the tracking distance to the selected unit
  const distanceUnitOnChange = (event) => {
    const oldUnit = distanceUnits.find(unit => unit.id === distanceInput.unit)?.name;
    const newUnit = distanceUnits.find(unit => unit.id === event.target.value)?.name;
    const convertedDist = convertDistance(distanceInput.value, oldUnit, newUnit);
    if (convertedDist !== null) {
      setValue(`${formNameBase}.trackDistance`, displayNumber(convertedDist));
    }
  }


  return (
    <>
      {/* patrol-type id */}
      <HiddenInput 
        name={`patrol.patrolTypes[${patrolIndex}].id`} 
        defaultValue={isAdd ? '' : undefined}
      />

      {/* type */}
      <HiddenInput 
        name={`patrol.patrolTypes[${patrolIndex}].type`}
        defaultValue={isAdd ? Types.Patrol.TRACKING : undefined}
      />

      {/* order */}
      <HiddenInput 
        name={`patrol.patrolTypes[${patrolIndex}].order`} 
        defaultValue={isAdd ? patrolIndex + 1 : undefined}
      />

      {/* tracking id */}
      <HiddenInput 
        name={`patrol.patrolTypes[${patrolIndex}].tracking.id`} 
        defaultValue={isAdd ? '' : undefined}
      />


      <EditorIconSection
        iconName='patrol-tracking'
        iconMarginTop={6}
        helpProps={{
          translation: edit.translation,
          section: 'tracking'
        }}
      >

        {isTrackActive &&
          <Alert severity='error' sx={{ marginBottom: 2 }}>
            <Typography>
              IMPORTANT NOTE: This track wasn't saved when it was stopped and may not be done.
              To prevent data loss, please save this track in the PACKTRACK mobile app first before completing it here.
            </Typography>
          </Alert>
        }

        {/* terrain types */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('tracking.terrainTypes.helpTitle')}
          text={t('tracking.terrainTypes.helpInfo')}
        >
          {focusHandlers =>
            <FormGridContainer>
              <FormGridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <CheckboxList
                  name={`${formNameBase}.terrainTypes`}
                  label={t('tracking.terrainTypes.label')}
                  dataTestId='tracking-terrain-types'
                  options={edit.options.terrainTypes}
                  customOther
                  focusHandlers={focusHandlers}
                  rules={{
                    validate: val => (val && val.length > 0) || t('tracking.terrainTypes.errMsgEmpty')
                  }}
                />
              </FormGridItem>
            </FormGridContainer>}
        </HelpTextItem>


        {/* tracking data */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('tracking.trackingData.helpTitle')}
          text={t('tracking.trackingData.helpInfo')}
          style={{ marginTop: 16, minHeight: 32 }}
        >
          <FormSectionTitle>
            {t('tracking.trackingData.label')}
          </FormSectionTitle>
        </HelpTextItem>


        {/* track distance */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('tracking.distance.helpTitle')}
          text={t('tracking.distance.helpInfo')}
        >
          {focusHandlers =>
            <FormGridContainer>

              {/* distance */}
              <FormGridItem xs={6} sm={6}>
                <TextField
                  name={`${formNameBase}.trackDistance`}
                  label={t('tracking.distance.label')}                  
                  dataTestId='tracking-distance'
                  defaultValue={isAdd ? '' : undefined}
                  type='number'
                  focusHandlers={focusHandlers}
                  rules={{
                    required: t('tracking.distance.errMsgEmpty'),
                    max: {
                      value: 999999999,
                      message: t('tracking.distance.errMsgSize')
                    },
                  }}
                  onChange={e => setDistanceInput({
                    value: e.target.value,
                    unit: distanceUnitValue?.id
                  })}
                />
              </FormGridItem>

              {/* distance unit */}
              <FormGridItem xs={6} sm={6}>
                <Select
                  name={`${formNameBase}.trackDistanceUnit`}
                  options={edit.options.distanceUnits}
                  dataTestId='tracking-distance-unit'
                  defaultValue={isAdd ? edit.options.distanceUnits[0] : undefined}
                  focusHandlers={focusHandlers}
                  style={{ marginTop: 4 }}
                  setValueAs={id => ({ id })}
                  rules={{ required: t('tracking.distance.errMsgUnitEmpty') }}
                  onChange={distanceUnitOnChange}
                />
              </FormGridItem>

            </FormGridContainer>}
        </HelpTextItem>


        {/* track duration */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('tracking.duration.helpTitle')}
          text={t('tracking.duration.helpInfo')}
        >
          {focusHandlers =>
            <FormGridContainer>
              <FormGridItem>
                <TextField
                  name={`${formNameBase}.trackDuration`}
                  label={t('tracking.duration.label')}
                  dataTestId='tracking-duration-minutes'
                  defaultValue={isAdd ? '' : undefined}
                  type='number'
                  focusHandlers={focusHandlers}
                  rules={{
                    required: t('tracking.duration.errMsgEmpty'),
                    max: {
                      value: 999999999,
                      message: t('tracking.duration.errMsgSize')
                    },
                  }}
                />
              </FormGridItem>
            </FormGridContainer>}
        </HelpTextItem>


        <EditorAccordion
          name="optional fields"
          summary={<Typography variant="body2">OPTIONAL: Tracking, Obstruction</Typography>}
          data-testid='optional-tracking-accordion'
          detail={
            <>

              {/* track times */}
              <HelpTextItem
                sidebarText={drawerState.sidebarText}
                title={t('tracking.times.helpTitle')}
                text={t('tracking.times.helpInfo')}
              >
                {focusHandlers =>
                  <FormGridContainer>

                    {/* track laid time */}
                    <FormGridItem>
                      <DateTimePicker
                        variant='time'
                        name={`${formNameBase}.trackLaidTime`}
                        label={t('tracking.times.labelLaid')}
                        dataTestId='tracking-laid-time'
                        defaultValue={parseISO(edit.record.date)}
                        focusHandlers={focusHandlers}
                        clearable
                        setValueAs={date => {
                          if (date && isValid(date)) {
                            // combine record date with time input
                            const eventDate = parseISO(edit.record.date);
                            const combined = new Date(
                              eventDate.getFullYear(), 
                              eventDate.getMonth(), 
                              eventDate.getDate(), 
                              date.getHours(), 
                              date.getMinutes());
                            return formatISO9075(combined); // no-timezone string
                          }
                          return date;
                        }} 
                        format='HH:mm'
                      />
                    </FormGridItem>

                    {/* track followed time */}
                    <FormGridItem>
                      <DateTimePicker
                        variant='time'
                        name={`${formNameBase}.trackFollowedTime`}
                        label={t('tracking.times.labelFollowed')}
                        dataTestId='tracking-followed-time'
                        focusHandlers={focusHandlers}
                        clearable
                        defaultValue={parseISO(edit.record.date)}
                        setValueAs={date => {
                          if (date && isValid(date)) {
                            // combine record date with time input
                            const eventDate = parseISO(edit.record.date);
                            const combined = new Date(
                              eventDate.getFullYear(), 
                              eventDate.getMonth(), 
                              eventDate.getDate(), 
                              date.getHours(), 
                              date.getMinutes());
                            return formatISO9075(combined); // no-timezone string
                          }
                          return date;
                        }} 
                        format='HH:mm'
                      />
                    </FormGridItem>

                </FormGridContainer>}
              </HelpTextItem>


              {/* turns */}
              <HelpTextItem
                sidebarText={drawerState.sidebarText}
                title={t('tracking.turns.helpTitle')}
                text={t('tracking.turns.helpInfo')}
              >
                {focusHandlers =>
                  <FormGridContainer>
                    <FormGridItem>
                      <TextField
                        name={`${formNameBase}.trackTotalTurns`}
                        label={t('tracking.turns.label')}
                        dataTestId='tracking-total-turns'
                        defaultValue={isAdd ? '' : undefined}
                        focusHandlers={focusHandlers}
                        type='number'
                        rules={{
                          max: {
                            value: 999999999,
                            message: t('tracking.turns.errMsgSize')
                          },
                        }}
                      />
                    </FormGridItem>
                  </FormGridContainer>}
              </HelpTextItem>


              {/* humans crossing */}
              <HelpTextItem
                sidebarText={drawerState.sidebarText}
                title={t('tracking.humansCrossing.helpTitle')}
                text={t('tracking.humansCrossing.helpInfo')}
              >
                {focusHandlers =>
                  <FormGridContainer>
                    <FormGridItem>
                      <TextField
                        name={`${formNameBase}.trackTotalHumansCrossing`}
                        label={t('tracking.humansCrossing.label')}
                        dataTestId='tracking-total-humans-crossing'
                        defaultValue={isAdd ? '' : undefined}
                        focusHandlers={focusHandlers}
                        type='number'
                        rules={{
                          max: {
                            value: 999999999,
                            message: t('tracking.humansCrossing.errMsgSize')
                          },
                        }}
                      />
                    </FormGridItem>
                  </FormGridContainer>}
              </HelpTextItem>


              {/* animals on track */}
              <HelpTextItem
                sidebarText={drawerState.sidebarText}
                title={t('tracking.animalsOnTrack.helpTitle')}
                text={t('tracking.animalsOnTrack.helpInfo')}
              >
                {focusHandlers =>
                  <FormGridContainer>
                    <FormGridItem>
                      <TextField
                        name={`${formNameBase}.trackTotalAnimalsOnTrack`}
                        label={t('tracking.animalsOnTrack.label')}
                        dataTestId='tracking-total-animals-on-track'
                        defaultValue={isAdd ? '' : undefined}
                        focusHandlers={focusHandlers}
                        type='number'
                        rules={{
                          max: {
                            value: 999999999,
                            message: t('tracking.animalsOnTrack.errMsgSize')
                          },
                        }}
                      />
                    </FormGridItem>
                  </FormGridContainer>}
              </HelpTextItem>


              {/* obstruction types */}
              <HelpTextItem
                sidebarText={drawerState.sidebarText}
                title={t('tracking.obstructionTypes.helpTitle')}
                text={t('tracking.obstructionTypes.helpInfo')}
              >
                {focusHandlers =>
                  <FormGridContainer>
                    <FormGridItem xs={12} sm={12} md={12} lg={12}>
                      <CheckboxList
                        name={`${formNameBase}.obstructionTypes`}
                        label={t('tracking.obstructionTypes.label')}
                        dataTestId='tracking-obstruction-types'
                        options={edit.options.obstructionTypes}
                        customOther
                        focusHandlers={focusHandlers}
                      />
                    </FormGridItem>
                  </FormGridContainer>}
              </HelpTextItem>
            </>
          }
        />

      </EditorIconSection>

      {tracks &&
        <DeploymentMap
          recordId={edit.record.id}
          trackingRecord={trackingRecord}
          tracks={tracks}
        />
      }

      <PatrolArrestsBitesEdit {...props} />
    </>
  );
}